import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import Swal from 'sweetalert2'
import {PUT, GET} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {useNavigate, useParams} from 'react-router-dom'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'

export enum TypeEntertainment {
  ENTERTAINMENT_EXPERIENCE = 'entertainment_experience',
  ENTERTAINMENT_ACTIVITY = 'entertainment_activity',
  ENTERTAINMENT_TOUR = 'entertainment_tour',
}

const getTypeLabel = (type: TypeEntertainment): string => {
  const labels = {
    [TypeEntertainment.ENTERTAINMENT_EXPERIENCE]: i18n.t('entertainment_services.type_experience'),
    [TypeEntertainment.ENTERTAINMENT_ACTIVITY]: i18n.t('entertainment_services.type_activity'),
    [TypeEntertainment.ENTERTAINMENT_TOUR]: i18n.t('entertainment_services.type_tour'),
  }
  return labels[type] || type
}

const UpdateEntertainmentServiceContent: FC = () => {
  const navigate = useNavigate()
  const {id} = useParams<{id: string}>()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    type: TypeEntertainment.ENTERTAINMENT_EXPERIENCE,
    status: true,
  })

  // Carregar dados do serviço
  useEffect(() => {
    const getService = async () => {
      try {
        setLoading(true)
        const res = await GET(`/entertainment-service/${id}`)
        const service = res.data[0]

        setFormData({
          title: service.title || '',
          type: service.type || TypeEntertainment.ENTERTAINMENT_EXPERIENCE,
          status: service.status !== false,
        })

        setLoading(false)
      } catch (e) {
        setLoading(false)
        console.log(e)
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('entertainment_services.msg_error_load')),
          text: String(i18n.t('entertainment_services.msg_error_load_text')),
        }).then(() => navigate('/management/entertainment'))
      }
    }

    if (id) {
      getService()
    }
  }, [id, navigate])

  const handleInputChange = (field: string, value: string | TypeEntertainment | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const validateForm = () => {
    if (!formData.title.trim()) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('entertainment_services.msg_error_update')),
        text: String(i18n.t('entertainment_services.msg_error_required_title')),
      })
      return false
    }

    if (formData.title.length > 255) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('entertainment_services.msg_error_update')),
        text: String(i18n.t('entertainment_services.msg_error_title_length')),
      })
      return false
    }

    return true
  }

  const updateService = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)

      const payload = {
        title: formData.title.trim(),
        type: formData.type,
        status: formData.status,
      }

      const response = await PUT(`/entertainment-service/${id}`, payload)

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('entertainment_services.btn_update')),
          text: String(i18n.t('entertainment_services.msg_success_update')),
        }).then(() => {
          navigate('/management/entertainment')
        })
      } else {
        throw new Error(response.message || i18n.t('entertainment_services.msg_error_update'))
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('entertainment_services.msg_error_update')),
        text: String(e.message || i18n.t('entertainment_services.msg_error_update')),
      })
      console.log(e)
    }
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='d-flex justify-content-between flex-wrap'>
            <div style={{flex: 1}}>
              <div className='w-100 card'>
                <div className='card-header py-5'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder fs-3 mb-1'>
                      {i18n.t('entertainment_services.edit')}
                    </span>
                    <BreadCrumbs />
                    <span className='text-muted mt-1 fw-bold fs-7'>
                      {i18n.t('entertainment_services.edit_subtitle')}
                    </span>
                  </h3>
                </div>
                <div className='card-body'>
                  <div className='row'>
                    <div className='col-lg-8'>
                      <Input
                        label={i18n.t('entertainment_services.field_title')}
                        inputAttr={{
                          type: 'text',
                          placeholder: i18n.t('entertainment_services.field_title_placeholder'),
                          maxLength: 255,
                        }}
                        name='title'
                        value={formData.title}
                        change={(value: string) => handleInputChange('title', value)}
                        maxCaracteres={255}
                      />
                    </div>
                    <div className='col-lg-4'>
                      <div className='mb-3 mt-6'>
                        <label className='form-label'>
                          {i18n.t('entertainment_services.field_type')}
                        </label>
                        <select
                          className='form-select'
                          value={formData.type}
                          onChange={(e) =>
                            handleInputChange('type', e.target.value as TypeEntertainment)
                          }
                        >
                          {Object.values(TypeEntertainment).map((type) => (
                            <option key={type} value={type}>
                              {getTypeLabel(type)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className='row'>
                    <div className='col-lg-12'>
                      <Input
                        inputAttr={{
                          type: 'checkbox',
                        }}
                        label={i18n.t('entertainment_services.field_status')}
                        name='status'
                        checked={formData.status}
                        change={() => handleInputChange('status', !formData.status)}
                      />
                    </div>
                  </div>

                  <div className='d-flex justify-content-end mt-5'>
                    <Button
                      text={i18n.t('buttons.back')}
                      color='info'
                      size='small'
                      click={() => navigate('/management/entertainment')}
                      btnClass='me-3'
                    />
                    <Button
                      size='small'
                      text={i18n.t('buttons.save')}
                      color='primary'
                      click={updateService}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const UpdateEntertainmentService: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t('general.dashboard'), path: 'dashboard', isActive: true},
          {
            title: i18n.t('entertainment_services.list_title'),
            path: 'management/entertainment',
            isActive: true,
          },
          {
            title: i18n.t('entertainment_services.edit_title'),
            path: 'management/entertainment/update',
            isActive: true,
          },
        ]}
      >
        {i18n.t('entertainment_services.edit_title')}
      </PageTitle>
      <UpdateEntertainmentServiceContent />
    </>
  )
}

export {UpdateEntertainmentService}
